import { prepareDocx, type PreparedDocx, type TableRecoveryProof } from '@hector/docx-integrity';
import JSZip from 'jszip';

interface PublicDocument {
  blocks: { list(input: { offset: number; limit: number; includeText: true }): unknown };
  trackChanges: { list(input: { in: 'all'; offset: 0; limit: 1 }): unknown };
}

const WORD_SETTINGS = 'word/settings.xml';
const WORD_TRACK_REVISIONS = /<w:trackRevisions\b[^>]*?(?:\/>|>[\s\S]*?<\/w:trackRevisions>)/g;

const MAX_PROOF_BLOCKS = 10_000;
const PAGE_SIZE = 200;

function record(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function publicDocument(instance: unknown): PublicDocument | null {
  if (!record(instance) || !record(instance.activeEditor) || !record(instance.activeEditor.doc)) return null;
  const doc = instance.activeEditor.doc;
  if (!record(doc.blocks) || typeof doc.blocks.list !== 'function'
    || !record(doc.trackChanges) || typeof doc.trackChanges.list !== 'function') return null;
  return doc as unknown as PublicDocument;
}

/** Missing API, pending revisions or an incomplete page is not evidence of zero. */
async function zeroRevisionSnapshot(doc: PublicDocument): Promise<string | null> {
  const result = await doc.trackChanges.list({ in: 'all', offset: 0, limit: 1 });
  return record(result) && result.total === 0 && Array.isArray(result.items) && result.items.length === 0
    && typeof result.evaluatedRevision === 'string' ? result.evaluatedRevision : null;
}

/**
 * A table may be gone in the public model while export retains orphan closings.
 * Enumerate every surviving paragraph and prove a stable model; OOXML alone
 * cannot distinguish that case from lost table openings (#1516).
 */
async function tableRecoveryProof(instance: unknown): Promise<TableRecoveryProof | null> {
  const doc = publicDocument(instance);
  if (!doc) return null;
  const beforeRevisions = await zeroRevisionSnapshot(doc);
  if (beforeRevisions === null) return null;
  const paragraphTexts: string[] = [];
  const ids = new Set<string>();
  let total: number | undefined;
  let revision: string | undefined;
  do {
    const page = await doc.blocks.list({ offset: ids.size, limit: PAGE_SIZE, includeText: true });
    if (!record(page) || !Array.isArray(page.blocks) || typeof page.total !== 'number'
      || !Number.isSafeInteger(page.total) || page.total < 1 || page.total > MAX_PROOF_BLOCKS
      || typeof page.revision !== 'string'
      || (total !== undefined && (total !== page.total || revision !== page.revision))
      || page.blocks.length === 0 || ids.size + page.blocks.length > page.total) return null;
    total = page.total;
    revision = page.revision;
    for (const block of page.blocks) {
      // These public block kinds correspond to body-level w:p. A table or an
      // unrecognized container cannot be silently flattened into its text.
      if (!record(block) || typeof block.nodeId !== 'string' || !block.nodeId || ids.has(block.nodeId)
        || !['paragraph', 'heading', 'listItem'].includes(String(block.nodeType))
        || typeof block.text !== 'string') return null;
      ids.add(block.nodeId);
      paragraphTexts.push(block.text);
    }
  } while (ids.size < total);
  const afterRevisions = await zeroRevisionSnapshot(doc);
  const anchor = await doc.blocks.list({ offset: 0, limit: 1, includeText: true });
  if (afterRevisions !== beforeRevisions || !record(anchor)
    || anchor.total !== total || anchor.revision !== revision) return null;
  return { paragraphTexts, tableCount: 0, trackedChangeCount: 0 };
}

/**
 * Word opens a file in track-changes mode when its settings carry
 * `w:trackRevisions`, and SuperDoc copies `word/settings.xml` through verbatim:
 * a Word imported with tracking on kept it in every Canvas download, whatever
 * the Canvas regime, so every edit the lawyer made in Word showed as a
 * revision (support Crisp, 2026-10-19; `canvas-telechargement-sans-suivi-word.spec.ts`).
 * The Canvas regime is a Canvas setting; the file leaves Word's own toggle off.
 */
async function withoutWordTrackRevisions(prepared: Exclude<PreparedDocx, { status: 'rejected' }>): Promise<PreparedDocx> {
  const archive = await JSZip.loadAsync(prepared.bytes);
  const entry = archive.file(WORD_SETTINGS);
  if (!entry) return prepared;
  const settings = await entry.async('string');
  const count = settings.match(WORD_TRACK_REVISIONS)?.length ?? 0;
  if (count === 0) return prepared;
  archive.file(WORD_SETTINGS, settings.replace(WORD_TRACK_REVISIONS, ''), {
    date: entry.date, comment: entry.comment, createFolders: false,
    unixPermissions: entry.unixPermissions, dosPermissions: entry.dosPermissions,
  });
  return {
    status: 'repaired',
    bytes: await archive.generateAsync({ type: 'uint8array', compression: 'DEFLATE' }),
    repairs: [...prepared.repairs, { code: 'WORD_TRACK_REVISIONS_SETTING', part: WORD_SETTINGS, count }],
  };
}

/**
 * Prepare the actual Canvas export; table repair additionally needs a public
 * live-model proof. Unsupported or inconsistent reads preserve the rejection.
 * Paragraph texts stay in memory and are never placed in logs or diagnostics.
 */
export async function prepareCanvasDocx(bytes: Uint8Array, instance: unknown): Promise<PreparedDocx> {
  const prepared = await prepareWithTableProof(bytes, instance);
  return prepared.status === 'rejected' ? prepared : withoutWordTrackRevisions(prepared);
}

async function prepareWithTableProof(bytes: Uint8Array, instance: unknown): Promise<PreparedDocx> {
  const prepared = await prepareDocx(bytes);
  if (prepared.status !== 'rejected' || prepared.issues.length !== 1
    || prepared.issues[0]?.code !== 'DOCX_ORPHAN_TABLE_CLOSURES') return prepared;
  try {
    const proof = await tableRecoveryProof(instance);
    if (!proof) return prepared;
    return await prepareDocx(bytes, { tableRecoveryProof: proof });
  } catch {
    // A read failure must never become a guessed empty model or a guessed fix.
    return prepared;
  }
}
